import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useProject } from '../contexts/ProjectContext';
import ColumnMapper from '../components/ColumnMapper';
import KnowledgeSection from '../components/KnowledgeSection';
import api from '../utils/api';

// ── Users section ─────────────────────────────────────────────────────────────

const SUPERADMIN = 'alex.barshai@emew.com';

function UsersSection() {
  const { user } = useAuth();
  const [users, setUsers]     = useState([]);
  const [email, setEmail]     = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [newWeekly, setNewWeekly] = useState(false);
  const [newAlerts, setNewAlerts] = useState(false);
  // Digest send/preview state, keyed by digest kind ('weekly' | 'alerts')
  const [digestBusy, setDigestBusy] = useState(null);
  const [digestMsg, setDigestMsg]   = useState('');
  const [digestPreview, setDigestPreview] = useState(null);
  const [error, setError]     = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  // pending permission edits: { [email]: { is_admin } }
  const [pending, setPending] = useState({});
  const [saving, setSaving]   = useState(null); // email being saved

  const fetchUsers = async () => {
    try {
      const res = await api.get('/api/admin/users');
      setUsers(res.data);
      setPending({});
    } catch {
      setError('Failed to load users');
    }
  };

  useEffect(() => { fetchUsers(); }, []);

  const addUser = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    if (!email.trim()) return;
    setLoading(true);
    try {
      await api.post('/api/admin/users', {
        email: email.trim(),
        is_admin: isAdmin,
        digest_weekly: newWeekly,
        digest_alerts: newAlerts,
      });
      setSuccess(`Added ${email.trim()}`);
      setEmail(''); setIsAdmin(false); setNewWeekly(false); setNewAlerts(false);
      fetchUsers();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to add user');
    } finally {
      setLoading(false);
    }
  };

  const removeUser = async (targetEmail) => {
    if (!window.confirm(`Remove ${targetEmail}?`)) return;
    try {
      await api.delete(`/api/admin/users/${encodeURIComponent(targetEmail)}`);
      fetchUsers();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to remove user');
    }
  };

  // Editable per-user flags. Listed explicitly so a pending edit to one field
  // always carries the current value of the others through to the PATCH.
  const EDITABLE = ['is_admin', 'digest_weekly', 'digest_alerts'];

  // Track a permission / subscription change before saving
  const markPending = (targetEmail, field, value) => {
    const base = users.find(u => u.email === targetEmail) || {};
    setPending(prev => {
      const current = prev[targetEmail] || {};
      const merged = {};
      EDITABLE.forEach(f => {
        merged[f] = current[f] !== undefined ? current[f] : !!base[f];
      });
      merged[field] = value;
      return { ...prev, [targetEmail]: merged };
    });
  };

  const savePermissions = async (targetEmail) => {
    let changes = pending[targetEmail];
    if (!changes) return;
    // The superadmin's privileges are locked server-side; send only the
    // subscription fields so the request isn't rejected outright.
    if (targetEmail === SUPERADMIN) {
      changes = { digest_weekly: changes.digest_weekly, digest_alerts: changes.digest_alerts };
    }
    setSaving(targetEmail);
    setError('');
    try {
      await api.patch(`/api/admin/users/${encodeURIComponent(targetEmail)}`, changes);
      await fetchUsers();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to save permissions');
    } finally {
      setSaving(null);
    }
  };

  const isDirty = (targetEmail) => !!pending[targetEmail];

  // Recipients are whoever has that digest ticked — shown so you know who a
  // "Send now" will actually reach before pressing it.
  const digestRecipients = (field) =>
    users.filter(u => getVal(u, field)).map(u => u.email);

  const previewDigest = async (kind) => {
    setDigestBusy(`${kind}-preview`); setDigestMsg(''); setDigestPreview(null);
    try {
      const res = await api.post(`/api/admin/digest/${kind}/preview`);
      setDigestPreview({ kind, ...res.data });
    } catch (e) {
      setDigestMsg(e.response?.data?.detail || 'Preview failed');
    } finally {
      setDigestBusy(null);
    }
  };

  const sendDigest = async (kind, label, recipients) => {
    if (!recipients.length) {
      setDigestMsg(`No recipients — tick "${label}" for at least one user below.`);
      return;
    }
    if (!window.confirm(`Send the ${label} now to:\n\n${recipients.join('\n')}`)) return;
    setDigestBusy(`${kind}-send`); setDigestMsg('');
    try {
      const res = await api.post(`/api/admin/digest/${kind}/send`);
      setDigestMsg(res.data?.skipped
        ? `Not sent — ${res.data.skipped}`
        : `Sent to ${(res.data?.to || []).join(', ')}`);
    } catch (e) {
      setDigestMsg(e.response?.data?.detail || 'Send failed');
    } finally {
      setDigestBusy(null);
    }
  };

  // Get effective value for display (pending override or DB value)
  const getVal = (u, field) =>
    pending[u.email]?.[field] !== undefined
      ? pending[u.email][field]
      : !!u[field];

  return (
    <>
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-header">
          <span className="card-title">Add User</span>
        </div>
        <div className="card-body">
          {error && <div className="alert alert-error">{error}</div>}
          {success && <div className="alert alert-success">{success}</div>}
          <form onSubmit={addUser}>
            <div className="add-user-form">
              <div className="form-group">
                <div className="field-label">Email address</div>
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="name@emew.com"
                  required
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <label className="perm-checkbox-label">
                  <input
                    type="checkbox"
                    checked={isAdmin}
                    onChange={e => setIsAdmin(e.target.checked)}
                    style={{ width: 'auto' }}
                  />
                  Admin
                </label>
                <label className="perm-checkbox-label">
                  <input
                    type="checkbox"
                    checked={newWeekly}
                    onChange={e => setNewWeekly(e.target.checked)}
                    style={{ width: 'auto' }}
                  />
                  Weekly digest
                </label>
                <label className="perm-checkbox-label">
                  <input
                    type="checkbox"
                    checked={newAlerts}
                    onChange={e => setNewAlerts(e.target.checked)}
                    style={{ width: 'auto' }}
                  />
                  Data alerts
                </label>
              </div>
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? 'Adding…' : 'Add User'}
              </button>
            </div>
          </form>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-header">
          <span className="card-title">Email Digests</span>
        </div>
        <div className="card-body">
          {digestMsg && (
            <div className={digestMsg.startsWith('Sent') ? 'alert alert-success' : 'alert alert-error'}>
              {digestMsg}
            </div>
          )}
          {[
            { kind: 'weekly', field: 'digest_weekly', label: 'Weekly plant snapshot',
              when: 'Mondays 12:30 UTC', note: 'Snapshot, cell degradation assessment and AI insight per plant.' },
            { kind: 'alerts', field: 'digest_alerts', label: 'Data processing alerts',
              when: 'Daily 09:15 UTC', note: 'Files delivered to S3 or email that did not reach the dashboard.' },
          ].map(d => {
            const to = digestRecipients(d.field);
            return (
              <div key={d.kind} style={{
                display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
                padding: '10px 0', borderBottom: '1px solid #eef2f5',
              }}>
                <div style={{ flex: '1 1 260px', minWidth: 240 }}>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{d.label}</div>
                  <div style={{ fontSize: 11, color: '#6b7a8d' }}>{d.when} · {d.note}</div>
                  <div style={{ fontSize: 11, marginTop: 3 }}>
                    {to.length
                      ? <>Recipients: <strong>{to.join(', ')}</strong></>
                      : <span style={{ color: '#c0392b' }}>No recipients — tick the column below</span>}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button
                    className="btn btn-ghost btn-sm"
                    disabled={!!digestBusy}
                    onClick={() => previewDigest(d.kind)}
                  >
                    {digestBusy === `${d.kind}-preview` ? 'Building…' : 'Preview'}
                  </button>
                  <button
                    className="btn btn-primary btn-sm"
                    disabled={!!digestBusy || !to.length}
                    onClick={() => sendDigest(d.kind, d.label, to)}
                  >
                    {digestBusy === `${d.kind}-send` ? 'Sending…' : 'Send now'}
                  </button>
                </div>
              </div>
            );
          })}
          {digestPreview && (
            <div style={{ marginTop: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ fontSize: 12, color: '#6b7a8d' }}>
                  Preview — <strong>{digestPreview.subject}</strong>
                </div>
                <button className="btn btn-ghost btn-sm" onClick={() => setDigestPreview(null)}>Close</button>
              </div>
              <iframe
                title="digest preview"
                srcDoc={digestPreview.html}
                style={{
                  width: '100%', height: 460, marginTop: 6,
                  border: '1px solid #dfe4ea', borderRadius: 6, background: '#fff',
                }}
              />
            </div>
          )}
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <span className="card-title">Authorized Users</span>
          <span className="badge badge-blue">{users.length}</span>
        </div>
        <table className="user-table">
          <thead>
            <tr>
              <th>Email</th>
              <th style={{ textAlign: 'center' }}>Admin</th>
              <th style={{ textAlign: 'center' }} title="Monday plant snapshot + cell degradation assessment">
                Weekly<br /><span style={{ fontSize: 10, fontWeight: 400, color: '#6b7a8d' }}>digest</span>
              </th>
              <th style={{ textAlign: 'center' }} title="Alerts when delivered data fails to reach the dashboard">
                Data<br /><span style={{ fontSize: 10, fontWeight: 400, color: '#6b7a8d' }}>alerts</span>
              </th>
              <th>Added by</th>
              <th>Date</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {users.map(u => {
              const isSuperadmin = u.email === SUPERADMIN;
              const dirty = isDirty(u.email);
              return (
                <tr key={u.email} className={dirty ? 'row-dirty' : ''}>
                  <td>
                    <span style={{ fontWeight: u.email === user?.email ? 700 : 400 }}>{u.email}</span>
                    {u.email === user?.email && (
                      <span style={{ marginLeft: 6, fontSize: 10, color: '#6b7a8d' }}>you</span>
                    )}
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      checked={getVal(u, 'is_admin')}
                      disabled={isSuperadmin}
                      onChange={e => markPending(u.email, 'is_admin', e.target.checked)}
                      style={{ width: 'auto', cursor: isSuperadmin ? 'not-allowed' : 'pointer' }}
                    />
                  </td>
                  {/* Subscriptions are editable for everyone, superadmin included —
                      they are preferences, not privileges. */}
                  <td style={{ textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      checked={getVal(u, 'digest_weekly')}
                      onChange={e => markPending(u.email, 'digest_weekly', e.target.checked)}
                      style={{ width: 'auto', cursor: 'pointer' }}
                    />
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      checked={getVal(u, 'digest_alerts')}
                      onChange={e => markPending(u.email, 'digest_alerts', e.target.checked)}
                      style={{ width: 'auto', cursor: 'pointer' }}
                    />
                  </td>
                  <td style={{ color: '#6b7a8d', fontSize: 12 }}>{u.added_by || '—'}</td>
                  <td style={{ color: '#6b7a8d', fontSize: 12 }}>
                    {u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                      {dirty && (
                        <button
                          className="btn btn-primary btn-sm"
                          disabled={saving === u.email}
                          onClick={() => savePermissions(u.email)}
                        >
                          {saving === u.email ? 'Saving…' : 'Save'}
                        </button>
                      )}
                      {!isSuperadmin && (
                        <button
                          className="btn btn-ghost btn-sm"
                          style={{ color: '#dc3545', borderColor: 'transparent' }}
                          onClick={() => removeUser(u.email)}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ── Projects section ──────────────────────────────────────────────────────────

// ── S3 config inline panel ─────────────────────────────────────────────────────

function S3ConfigPanel({ projectId, projectName, initialPrefix }) {
  const [prefix, setPrefix] = useState(initialPrefix || '');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg]       = useState('');

  const save = async () => {
    setSaving(true); setMsg('');
    try {
      await api.put(`/api/projects/${projectId}/s3-config`, { s3_prefix: prefix });
      setMsg('Saved');
    } catch (e) {
      setMsg(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ padding: '12px 16px', borderTop: '1px solid #1e2a3a' }}>
      <div className="field-label" style={{ marginBottom: 6 }}>S3 folder prefix for {projectName}</div>
      <div style={{ fontSize: 11, color: '#6b7a8d', marginBottom: 8 }}>
        Bucket: <code style={{ color: '#a0b0c0' }}>emew-measurements-data-xf7rgyz6</code>
        &nbsp;·&nbsp;Available folders: Bioscope/, Conticon/, Rand/, Sircel (90 Cells)/
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <input
          type="text"
          value={prefix}
          onChange={e => { setPrefix(e.target.value); setMsg(''); }}
          placeholder="e.g. Rand/"
          style={{ flex: 1, maxWidth: 300, padding: '6px 10px', fontSize: 13 }}
        />
        <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </button>
        {msg && <span style={{ fontSize: 12, color: msg === 'Saved' ? '#38c172' : '#dc3545' }}>{msg}</span>}
      </div>
    </div>
  );
}

// ── Projects section ──────────────────────────────────────────────────────────

function ProjectsSection() {
  const { projects, fetchProjects, currentProject, setCurrentProject } = useProject();
  const [newName, setNewName]       = useState('');
  const [editId, setEditId]         = useState(null);
  const [editName, setEditName]     = useState('');
  const [error, setError]           = useState('');
  const [success, setSuccess]       = useState('');
  const [loading, setLoading]       = useState(false);
  const [mapperForId, setMapperForId] = useState(null); // project id whose mapper is open
  const [s3ForId, setS3ForId]         = useState(null); // project id whose S3 panel is open

  const addProject = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    if (!newName.trim()) return;
    setLoading(true);
    try {
      await api.post('/api/projects', { name: newName.trim() });
      setSuccess(`Created project "${newName.trim()}"`);
      setNewName('');
      await fetchProjects();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to create project');
    } finally {
      setLoading(false);
    }
  };

  const saveRename = async (id) => {
    if (!editName.trim()) return;
    setError('');
    try {
      await api.patch(`/api/projects/${id}`, { name: editName.trim() });
      const updated = await fetchProjects();
      // Update current project name if it was the one renamed
      if (currentProject?.id === id) {
        const fresh = (updated || projects).find(p => p.id === id);
        if (fresh) setCurrentProject(fresh);
      }
      setEditId(null); setEditName('');
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to rename project');
    }
  };

  const deleteProject = async (project) => {
    if (!window.confirm(`Delete project "${project.name}" and all its uploads? This cannot be undone.`)) return;
    setError('');
    try {
      await api.delete(`/api/projects/${project.id}`);
      const remaining = await fetchProjects();
      // If we deleted the current project, switch to first available
      if (currentProject?.id === project.id && remaining?.length) {
        setCurrentProject(remaining[0]);
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to delete project');
    }
  };

  return (
    <>
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-header">
          <span className="card-title">Add Project</span>
        </div>
        <div className="card-body">
          {error && <div className="alert alert-error">{error}</div>}
          {success && <div className="alert alert-success">{success}</div>}
          <form onSubmit={addProject}>
            <div className="add-user-form">
              <div className="form-group">
                <div className="field-label">Project name</div>
                <input
                  type="text"
                  value={newName}
                  onChange={e => setNewName(e.target.value)}
                  placeholder="e.g. Rand Refinery"
                  required
                />
              </div>
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? 'Creating…' : 'Create Project'}
              </button>
            </div>
          </form>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <span className="card-title">Projects</span>
          <span className="badge badge-blue">{projects.length}</span>
        </div>
        <table className="user-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Created</th>
              <th>Created by</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {projects.map(p => (
              <tr key={p.id}>
                <td>
                  {editId === p.id ? (
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <input
                        value={editName}
                        onChange={e => setEditName(e.target.value)}
                        style={{ padding: '4px 8px', fontSize: 13, width: 200 }}
                        autoFocus
                        onKeyDown={e => {
                          if (e.key === 'Enter') saveRename(p.id);
                          if (e.key === 'Escape') { setEditId(null); setEditName(''); }
                        }}
                      />
                      <button className="btn btn-primary btn-sm" onClick={() => saveRename(p.id)}>Save</button>
                      <button className="btn btn-ghost btn-sm" onClick={() => { setEditId(null); setEditName(''); }}>Cancel</button>
                    </div>
                  ) : (
                    <span style={{ fontWeight: currentProject?.id === p.id ? 700 : 400 }}>
                      {p.name}
                      {currentProject?.id === p.id && (
                        <span style={{ marginLeft: 6, fontSize: 10, color: '#6b7a8d' }}>current</span>
                      )}
                    </span>
                  )}
                </td>
                <td style={{ color: '#6b7a8d', fontSize: 12 }}>
                  {p.created_at ? new Date(p.created_at).toLocaleDateString() : '—'}
                </td>
                <td style={{ color: '#6b7a8d', fontSize: 12 }}>{p.created_by || '—'}</td>
                <td>
                  <div style={{ display: 'flex', gap: 4 }}>
                    {editId !== p.id && (
                      <>
                        <button
                          className="btn btn-ghost btn-sm"
                          onClick={() => { setEditId(p.id); setEditName(p.name); setMapperForId(null); setS3ForId(null); }}
                        >
                          Rename
                        </button>
                        <button
                          className={`btn btn-sm ${mapperForId === p.id ? 'btn-primary' : 'btn-ghost'}`}
                          onClick={() => { setMapperForId(mapperForId === p.id ? null : p.id); setS3ForId(null); }}
                        >
                          Columns
                        </button>
                        <button
                          className={`btn btn-sm ${s3ForId === p.id ? 'btn-primary' : 'btn-ghost'}`}
                          onClick={() => { setS3ForId(s3ForId === p.id ? null : p.id); setMapperForId(null); }}
                        >
                          S3
                        </button>
                      </>
                    )}
                    {p.id !== 1 && editId !== p.id && (
                      <button
                        className="btn btn-ghost btn-sm"
                        style={{ color: '#dc3545', borderColor: 'transparent' }}
                        onClick={() => deleteProject(p)}
                      >
                        Delete
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Column mapper panel */}
      {mapperForId && (() => {
        const p = projects.find(pr => pr.id === mapperForId);
        return p ? (
          <div className="card" style={{ marginTop: 16 }}>
            <ColumnMapper projectId={p.id} projectName={p.name} />
          </div>
        ) : null;
      })()}

      {/* S3 config panel */}
      {s3ForId && (() => {
        const p = projects.find(pr => pr.id === s3ForId);
        return p ? (
          <div className="card" style={{ marginTop: 16 }}>
            <div className="card-header">
              <span className="card-title">S3 Sync — {p.name}</span>
            </div>
            <S3ConfigPanel projectId={p.id} projectName={p.name} initialPrefix={p.s3_prefix || ''} />
          </div>
        ) : null;
      })()}
    </>
  );
}

// ── Main Admin page ───────────────────────────────────────────────────────────

const TABS = ['Users', 'Projects', 'Knowledge'];

export default function Admin() {
  const [tab, setTab] = useState('Users');

  return (
    <div className="admin-wrap">
      <div className="page-header">
        <div className="page-title">Administration</div>
        <div className="page-subtitle">Manage users and projects</div>
      </div>

      {/* Tab bar */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20 }}>
        {TABS.map(t => (
          <button
            key={t}
            className={`btn ${tab === t ? 'btn-primary' : 'btn-ghost'}`}
            onClick={() => setTab(t)}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === 'Users'     && <UsersSection />}
      {tab === 'Projects'  && <ProjectsSection />}
      {tab === 'Knowledge' && <KnowledgeSection />}
    </div>
  );
}
